import { AuctionHouseListProps } from 'Types/global.types.ts'
import { TFunction } from 'i18next'

export const API_URL: string = import.meta.env.VITE_API_URL as string
export const AUTH_BASE_API_URL = `${API_URL}/auth`
export const USER_BASE_API_URL = `${API_URL}/user`
export const SHIPPING_ADDRESS = `${API_URL}/shipping-address`
export const BUYER = `${API_URL}/buyer`
export const SELLER = `${API_URL}/seller`
export const NOTIFICATION_BASE_API_URL = `${API_URL}/notifications`
export const BLACKLIST_BASE_API_URL = `${API_URL}/blacklist`
export const REGISTRATION_BASE_API_URL = `${API_URL}/registration`
export const VIN_BASE_API_URL = `${API_URL}/vin`
export const CERTIFICATE_BASE_API_URL = `${API_URL}/certificate`
export const PURCHASE_BASE_API_URL = `${API_URL}/purchase`
export const USER_STORAGE_PLACE = `${API_URL}/storage-place`
export const USER_CUSTOMS_CROSSING = `${API_URL}/customs-crossing`
export const CONTACT_BASE_API_URL = `${API_URL}/contact`
export const CONTACT_AUTHENTICITY_BASE_API_URL = `${API_URL}/eur1-authenticity-request`
export const CUSTOM_TRANSACTION_STATUS_BASE_API_URL = `${API_URL}/customs-transaction`

/**
 * Auth API
 */
export const AUTH_API: {
  SIGN_IN_URL: string
  SIGN_UP_URL: string
  VERIFY_URL: string
  FORGOT_PASSWORD: string
  UPDATE_PASSWORD: string
  RESEND_EMAIL: string
} = {
  SIGN_IN_URL: `${AUTH_BASE_API_URL}/signin`,
  SIGN_UP_URL: `${AUTH_BASE_API_URL}/signup`,
  VERIFY_URL: `${AUTH_BASE_API_URL}/verify`,
  FORGOT_PASSWORD: `${AUTH_BASE_API_URL}/forgot-password`,
  UPDATE_PASSWORD: `${AUTH_BASE_API_URL}/update-password`,
  RESEND_EMAIL: `${AUTH_BASE_API_URL}/resend-email`,
}

export const AUTH_ERRORS: { UNEXPECTED: string; AXIOS: string } = {
  UNEXPECTED: 'An unexpected error occurred',
  AXIOS: 'Axios error: ',
}

/**
 * User API
 */
export const USER_API: {
  PROFILE_URL: string
  ONBOARDING_URL: string
  UPDATE_USER_PASSWORD: string
  SET_PRIMARY_SHIPPING_ADDRESS: string
} = {
  PROFILE_URL: `${USER_BASE_API_URL}/profile`,
  ONBOARDING_URL: `${USER_BASE_API_URL}/finish-onboarding`,
  UPDATE_USER_PASSWORD: `${USER_BASE_API_URL}/update-password`,
  SET_PRIMARY_SHIPPING_ADDRESS: `${USER_BASE_API_URL}/shipping-address`,
}

export const BUYER_API = {
  BUYER_SEARCH: `${BUYER}/search`,
}

export const SELLER_API = {
  SELLER_SEARCH: `${SELLER}/search`,
  SELLER_EORI: `${SELLER}/eori`,
  SELLER_BY_EORI: `${SELLER}/by-eori`,
}

export const REGISTRATION_API = {
  REGISTRATION_SEARCH: `${REGISTRATION_BASE_API_URL}/search`,
}

export const VIN_API = {
  VIN_SEARCH: `${VIN_BASE_API_URL}/search`,
}

export const CERTIFICATE_API = {
  CERTIFICATE_SEARCH: `${CERTIFICATE_BASE_API_URL}/search`,
}

/**
 * Notification API
 */
export const NOTIFICATION_API = {
  ROOT: NOTIFICATION_BASE_API_URL,
  UNREAD_COUNT: `${NOTIFICATION_BASE_API_URL}/unread-count`,
  READ_ALL: `${NOTIFICATION_BASE_API_URL}/read-all`,
  CREATE_EXPIRED: `${NOTIFICATION_BASE_API_URL}/create-expired`,
}

/**
 * Download API
 */
export const DOWNLOAD_API = {
  GET_FILE: `${API_URL}/download`,
}

export const DHL_API = {
  LIST: `${API_URL}/dhl`,
}

export const PAYMENT_API = {
  CERTIFICATE: `${API_URL}/certificate`,
  PURCHASE: `${API_URL}/purchase`,
  DHL: `${API_URL}/dhl`,
}

export const USER_TYPE = {
  physical: 'PHYSICAL',
  legal: 'LEGAL',
  forwarder: 'FORWARDER',
  super: 'SUPER',
  regular: 'REGULAR',
}

export const USER_ROLES = ['PHYSICAL', 'LEGAL', 'FORWARDER']

export const ADMIN_ROLES = ['SUPER', 'REGULAR']

export const CUSTOMS_ROLES = ['CUSTOMS_OFFICER']

export const MAX_UPLOAD_FILE = 50

export const MAX_CHARACTERS = 50
export const MIN_CHARACTERS = 8
export const MIN_REQUIRED_SEARCH_CHARACTERS = 3

export const AUCTION_HOUSE_LIST: Array<AuctionHouseListProps> = [
  {
    id: 'alcopa-auction-house',
    auctionHouseName: 'Alcopa',
    auctionHousePhone: '+33970719752',
    auctionHouseEmail: 'contact@alcopa-auction.fr',

    auctionHouseEori: '111222333444',
    auctionHouseAddress: 'test',
    auctionHouseStreet: 'test',
    auctionHousePostalCode: '21000',
    auctionHouseCity: 'test',
    auctionHouseCountry: 'test',

    description: 'Alcopa auction house',
    imageUrl: '/auction-houses/auction-house-alcopa.png',
  },
  {
    id: 'vpauto-auction-house',
    auctionHouseName: 'Vpauto',
    auctionHousePhone: '+38163111111',
    auctionHouseEmail: 'vpauto@gmail.com',

    auctionHouseEori: '111222333444',
    auctionHouseAddress: 'test',
    auctionHouseStreet: 'test',
    auctionHousePostalCode: '21000',
    auctionHouseCity: 'test',
    auctionHouseCountry: 'test',

    description: 'VPauto auction house',
    imageUrl: '/auction-houses/auction-house-vpcar.png',
  },
]

/**
 * Payment statuses
 */
export const CertificateState = {
  NotProcessed: 'NOT_PROCESSED',
  InProgress: 'IN_PROGRESS',
  Processed: 'PROCESSED',
  Reviewed: 'REVIEWED',
}

export const PurchaseSubjectState = {
  NotProcessed: 'NOT_PROCESSED',
  InProgress: 'IN_PROGRESS',
  Processed: 'PROCESSED',
  Reviewed: 'REVIEWED',
  Finalized: 'FINALIZED',
}

export const PaymentState = {
  NotPaid: 'NOT_PAID',
  Failed: 'FAILED',
  Paid: 'PAID',
  Monthly: 'MONTHLY',
  Monthly_generated: 'MONTHLY_GENERATED',
}

export const PurchaseState = {
  Active: 'ACTIVE',
  Finished: 'FINISHED',
}

export const AdminPurchaseState = {
  Active: 'ACTIVE',
  Finished: 'FINISHED',
  Failed: 'FAILED',
}

export const AdminOrderState = {
  NotAssigned: 'NOT ASSIGNED',
  InProgress: 'IN PROGRESS',
  InReview: 'IN REVIEW',
  Completed: 'COMPLETED',
}

export const WorkStatus = {
  Pending: 'PENDING',
  ReadyForReview: 'READY FOR REVIEW',
  InProgress: 'IN PROGRESS',
  InReview: 'IN REVIEW',
  Completed: 'COMPLETED',
}

export const mapAdminRoleName: { [key: string]: string } = {
  SUPER: 'Super admin',
  REGULAR: 'Admin',
}

export const PAYMENT_TYPE = {
  prepaid: 'PREPAID',
  postpaid: 'POSTPAID',
  card: 'CARD',
}

export const PAYMENT_TYPE_MAP = [
  {
    label: 'Prepaid',
    value: 'PREPAID',
  },
  {
    label: 'Postpaid',
    value: 'POSTPAID',
  },
  {
    label: 'Credit card',
    value: 'CARD',
  },
]

export const getBuyerOptions = (t: TFunction) => [
  {
    label: t('translation:common.label.physical'),
    value: 'PHYSICAL',
  },
  {
    label: t('translation:common.label.legal'),
    value: 'LEGAL',
  },
]

export const getPaymentTypeMapTranslation = (t: TFunction) => [
  {
    label: t('translation:common.label.prepaid'),
    value: 'PREPAID',
  },
  {
    label: t('translation:common.label.postpaid'),
    value: 'POSTPAID',
  },
  {
    label: t('translation:common.label.credit_card'),
    value: 'CARD',
  },
]

export const mapUserTypes = {
  PHYSICAL: 'Physical',
  LEGAL: 'Legal',
  FORWARDER: 'Forwarder',
}

export const getUserTypesTranslate = (t: TFunction) => ({
  PHYSICAL: t('translation:common.label.physical'),
  LEGAL: t('translation:common.label.legal'),
  FORWARDER: t('translation:common.label.forwarder'),
})

export const vinTypeMap: Record<string, string> = {
  Car: 'car',
  Bus: 'bus',
  Van: 'van',
  Truck: 'truck',
  Tractor: 'tractor',
  Trailer: 'trailer',
  Motorcycle: 'motorcycle',
  Motorcycles: 'motorcycle',
  'Construction Machine': 'construction_machine',
  Forklift: 'forklift',
}

export const fuelGroupMap: Record<string, string> = {
  Diesel: 'diesel',
  Petrol: 'petrol',
  Gasoline: 'petrol',
  Electric: 'electric',
  'Diesel-Hybrid': 'diesel_hybrid',
  'Petrol-Hybrid': 'petrol_hybrid',
}

export const nomenclatureEngineRanges: Record<string, Record<string, { min: number; max: number; code: string }[]>> = {
  car: {
    diesel: [
      { min: 0, max: 1500, code: '8703319000' },
      { min: 1501, max: 2500, code: '8703329000' },
      { min: 2501, max: Infinity, code: '8703339000' },
    ],
    petrol: [
      { min: 0, max: 1000, code: '8703219000' },
      { min: 1001, max: 1500, code: '8703229000' },
      { min: 1501, max: 3000, code: '8703239000' },
      { min: 3001, max: Infinity, code: '8703249000' },
    ],
    // diesel_hybrid: [{ min: 0, max: Infinity, code: '8703500000' }],
    // petrol_hybrid: [
    //   { min: 0, max: 1000, code: '8703401090' },
    //   { min: 1001, max: Infinity, code: '8703409090' },
    // ],
    electric: [{ min: 0, max: Infinity, code: '8703809000' }],
  },

  // bus: {
  //   diesel: [
  //     { min: 0, max: 2500, code: '8702109900' },
  //     { min: 2501, max: Infinity, code: '8702101900' },
  //   ],
  //   diesel_hybrid: [
  //     { min: 0, max: 2500, code: '8702209000' },
  //     { min: 2501, max: Infinity, code: '8702201000' },
  //   ],
  //   petrol: [
  //     { min: 0, max: 2800, code: '8702903900' },
  //     { min: 2801, max: Infinity, code: '8702901900' },
  //   ],
  //   petrol_hybrid: [
  //     { min: 0, max: 2800, code: '8702309000' },
  //     { min: 2801, max: Infinity, code: '8702301000' },
  //   ],
  //   electric: [{ min: 0, max: Infinity, code: '8702400000' }],
  // },

  van: {
    diesel: [
      { min: 0, max: 2500, code: '8704219900' },
      { min: 2501, max: Infinity, code: '8704213900' },
    ],
    // diesel_hybrid: [
    //   { min: 0, max: 2500, code: '8704419900' },
    //   { min: 2501, max: Infinity, code: '8704413900' },
    // ],
    petrol: [
      { min: 0, max: 2800, code: '8704319900' },
      { min: 2801, max: Infinity, code: '8704313900' },
    ],
    // petrol_hybrid: [
    //   { min: 0, max: 2800, code: '8704519900' },
    //   { min: 2801, max: Infinity, code: '8704513900' },
    // ],
  },

  // truck: {
  //   diesel: [{ min: 0, max: Infinity, code: '8701219000' }],
  //   diesel_hybrid: [{ min: 0, max: Infinity, code: '8701229000' }],
  //   petrol_hybrid: [{ min: 0, max: Infinity, code: '8701239000' }],
  //   electric: [{ min: 0, max: Infinity, code: '8701249000' }],
  // },

  // tractor: {
  //   diesel: [
  //     { min: 0, max: 18, code: '8701911000' },
  //     { min: 19, max: 37, code: '8701921000' },
  //     { min: 38, max: 75, code: '8701931000' },
  //     { min: 76, max: 130, code: '8701941000' },
  //     { min: 131, max: Infinity, code: '8701951000' },
  //   ],
  // },

  trailer: {
    diesel: [{ min: 0, max: Infinity, code: '8716398000' }],
  },

  // motorcycle: {
  //   petrol: [
  //     { min: 0, max: 50, code: '8711100000' },
  //     { min: 51, max: 125, code: '8711209200' },
  //     { min: 126, max: 250, code: '8711209800' },
  //     { min: 251, max: 380, code: '8711301000' },
  //     { min: 381, max: 500, code: '8711309000' },
  //     { min: 501, max: 800, code: '8711400000' },
  //     { min: 801, max: Infinity, code: '8711500000' },
  //   ],
  //   electric: [{ min: 0, max: Infinity, code: '8711900000' }],
  // },

  construction_machine: {
    diesel: [{ min: 0, max: Infinity, code: '8479100000' }],
  },

  forklift: {
    diesel: [{ min: 0, max: Infinity, code: '8427101000' }],
    petrol: [{ min: 0, max: Infinity, code: '8427201100' }],
  },
}

export const getLanguageList = (isAdmin: boolean) => [
  {
    label: 'EN',
    value: 'en',
  },
  {
    label: 'FR',
    value: 'fr',
  },
  ...(!isAdmin
    ? [
        {
          label: 'SR',
          value: 'sr',
        },
        {
          label: 'RU',
          value: 'ru',
        },
      ]
    : []),
]

export const HOME_PAGE_ROUTE = '/landing/home-page'

export const EXPORTATION_COUNTRIES = [
  {
    label: 'Serbia',
    value: 'RS',
  },
  {
    label: 'Bosnia and Herzegovina',
    value: 'BA',
  },
  {
    label: 'Kosovo',
    value: 'XK',
  },
  {
    label: 'North Macedonia',
    value: 'MK',
  },
  {
    label: 'Montenegro',
    value: 'ME',
  },
  {
    label: 'Tunisia',
    value: 'TN',
  },
  {
    label: 'Algeria',
    value: 'DZ',
  },
  {
    label: 'Morocco',
    value: 'MA',
  },
  {
    label: 'Moldova, Republic of',
    value: 'MD',
  },
  {
    label: `Ukraine`,
    value: `UA`,
  },
]
